import { Component, type ErrorInfo, type ReactNode } from "react";
import { captureException } from "../lib/shared/sentry";
import { isChunkLoadError } from "./ChunkErrorBoundary";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * React Error Boundary — catches rendering errors and shows a recovery UI
 * instead of a blank screen. Per PRD 4.12: "never show a blank screen or
 * unrecoverable error."
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // Let chunk load errors bubble up to ChunkErrorBoundary
    if (isChunkLoadError(error)) {
      throw error;
    }
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    captureException(error, {
      contexts: { react: { componentStack: errorInfo.componentStack ?? "" } },
    });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-[200px] flex items-center justify-center p-8">
          <div className="max-w-md text-center">
            <h2 className="text-lg font-semibold text-surface-900 dark:text-white mb-2">
              Something didn&apos;t load right
            </h2>
            <p className="text-sm text-surface-600 dark:text-surface-400 mb-4">
              This is on us, not you. Try refreshing, or head back to the dashboard.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => this.setState({ hasError: false, error: null })}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-md hover:bg-brand-700"
              >
                Refresh
              </button>
              <a
                href="/"
                className="px-4 py-2 text-sm font-medium text-brand-600 bg-brand-50 dark:bg-brand-900/30 dark:text-brand-400 rounded-md hover:bg-brand-100 dark:hover:bg-brand-900/50"
              >
                Back to dashboard
              </a>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
